import { formatBrl } from '@estac1/shared';
import { Badge, Button } from '@estac1/ui';
import type { UseMutationResult } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Archive, ArchiveRestore, Eye, Hourglass, RotateCcw } from 'lucide-react';
import { subscriptionStatusLabel } from '../../lib/status.js';
import { trialDaysLeft as computeTrialDaysLeft, TRIAL_SOON_DAYS } from '../../lib/trial.js';
import {
  LotModuleToggleButtons,
  type ModuleToggleMutation,
  type ToggleableModuleKey,
} from './lot-module-toggles.js';
import type { Lot, PricingSettings } from './types.js';
import { formatDate, mrrForLot, statusVariant } from './utils.js';

interface ClientLotRowProps {
  lot: Lot;
  pricing: PricingSettings | undefined;
  onExtend: (lot: Lot) => void;
  onArchive: (lot: Lot) => void;
  onUnarchive: (lot: Lot) => void;
  onResetCheckout: (lot: Lot) => void;
  onToggleModule: (lot: Lot, key: ToggleableModuleKey, enabled: boolean) => void;
  unarchive: UseMutationResult<unknown, unknown, string, unknown>;
  toggleModule: ModuleToggleMutation;
  resetCheckout: UseMutationResult<unknown, unknown, string, unknown>;
}

export const ClientLotRow = ({
  lot,
  pricing,
  onExtend,
  onArchive,
  onUnarchive,
  onResetCheckout,
  onToggleModule,
  unarchive,
  toggleModule,
  resetCheckout,
}: ClientLotRowProps) => {
  const mrr = mrrForLot(lot, pricing);
  const trialDaysLeft =
    lot.subscriptionStatus === 'trial' ? computeTrialDaysLeft(lot.trialEndsAt) : null;
  const trialSoon = trialDaysLeft !== null && trialDaysLeft <= TRIAL_SOON_DAYS;

  return (
    <li className="space-y-2 rounded border border-border bg-surface p-3 text-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="min-w-0">
          <Link
            to="/clients/$lotId"
            params={{ lotId: lot.id }}
            className="font-medium underline underline-offset-2"
          >
            {lot.name}
          </Link>
          <p className="text-xs text-muted-fg">
            {lot.city && lot.state ? `${lot.city} - ${lot.state} · ` : ''}
            {lot.vacancies != null ? `${lot.vacancies} vagas` : 'sem capacidade definida'}
            {mrr != null && ` · ${formatBrl(mrr)}/mês`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={statusVariant(lot.subscriptionStatus)}>
            {subscriptionStatusLabel(lot.subscriptionStatus)}
          </Badge>
          {lot.damagePhotosEnabled && <Badge variant="brand">fotos de avaria</Badge>}
          {lot.barrierEnabled && <Badge variant="brand">cancela</Badge>}
          {lot.printerEnabled && <Badge variant="brand">impressora</Badge>}
          {lot.trialEndsAt && (
            <Badge variant={trialSoon ? 'warning' : 'outline'}>
              {trialSoon && trialDaysLeft !== null
                ? trialDaysLeft <= 0
                  ? 'teste vencido'
                  : `vence em ${trialDaysLeft}d`
                : `até ${formatDate(lot.trialEndsAt)}`}
            </Badge>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {/* A página do cliente, com o "Ver painel como o dono" (#668). */}
        <Button asChild size="sm">
          <Link to="/clients/$lotId" params={{ lotId: lot.id }}>
            <Eye className="h-4 w-4" /> Abrir cliente
          </Link>
        </Button>
        {lot.archivedAt ? (
          <Button
            size="sm"
            variant="outline"
            disabled={unarchive.isPending}
            onClick={() => onUnarchive(lot)}
          >
            <ArchiveRestore className="h-4 w-4" /> Desarquivar
          </Button>
        ) : (
          <>
            <Button size="sm" variant="outline" onClick={() => onExtend(lot)}>
              <Hourglass className="h-4 w-4" /> Estender teste
            </Button>
            <LotModuleToggleButtons
              lot={lot}
              pending={toggleModule.isPending}
              onToggle={onToggleModule}
            />
            {(lot.subscriptionStatus === 'past_due' || lot.subscriptionStatus === 'canceled') && (
              <Button
                size="sm"
                variant="outline"
                disabled={resetCheckout.isPending}
                onClick={() => onResetCheckout(lot)}
              >
                <RotateCcw className="h-4 w-4" /> Resetar checkout
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => onArchive(lot)}>
              <Archive className="h-4 w-4" /> Arquivar
            </Button>
          </>
        )}
      </div>
    </li>
  );
};
